import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {ActivityContent, ActivityGranule, ActivityMetaEntityAttributes, ActivityReference, FillBlanksContent} from '@modules/activities/core/models';
import {MultimediaActivityGranule} from '@modules/activities/core/player-components/multimedia/multimedia.component';
import {TypologyLabel} from '@modules/activities/core/typologies/typology.label';
import {TypedDataEntityInterface} from 'shared/models/octopus-connect';
import {ShortAnswerActivityGranule} from '@modules/activities/core/models/activities/typologies/short-answer-activity.granule';
import {TranslateService} from "@ngx-translate/core";
import {get} from "lodash-es";

const DEFAULT_LANGUAGE = 'fr';

const ACTIVITY_NAME_LIMIT = 65;
const ACTIVITY_NAME_ENDING = '...';

const EXTERNAL_ACTIVITY_TRANSLATION_KEY = 'pretty_activity.typology_external_activity_label';

@Component({
    selector: 'app-pretty-activity-card',
    templateUrl: './pretty-activity-card.component.html',
    standalone: false
})
export class PrettyActivityCardComponent<T extends ActivityContent, U> implements OnChanges {
    @Input() activity!: ActivityGranule<T, U>;

    private translateService = inject(TranslateService)

    public metadatas: TypedDataEntityInterface<ActivityMetaEntityAttributes>;
    public activityReference: ActivityReference<U, T>;
    public typology: { id: string; label: TypologyLabel };
    public wording: string;
    public instruction: string;
    public activityContent: T;

    ngOnChanges(_changes: SimpleChanges): void {
        this.metadatas = this.activity?.getEmbed<ActivityMetaEntityAttributes>('metadatas') as TypedDataEntityInterface<ActivityMetaEntityAttributes>;
        this.activityReference = this.activity?.get('reference');
        this.typology = this.metadatas?.get('typology');
        this.wording = this.activityReference?.wording;
        this.instruction = this.activityReference?.instruction;
        this.activityContent = this.activityReference?.activity_content;
    }


    public activityName = (placeHolder?: boolean) => {
        let activityTitle = '';

        if (this.typology.label === TypologyLabel.external) {
            if (this.metadatas?.get('title')) {
                activityTitle = this.metadatas.get('title');
            }
            else
            activityTitle = this.translateService.instant(EXTERNAL_ACTIVITY_TRANSLATION_KEY);
        } else {

            const strDescription = this.getActivityDescription()

            activityTitle = strDescription.map(s => (s ?? '').replace(/<[^>]*>/g, ''))
            .join(' ');

        }
        if (placeHolder) {
            return activityTitle;
        }
        return activityTitle.length > ACTIVITY_NAME_LIMIT
            ? activityTitle.substring(0, ACTIVITY_NAME_LIMIT - ACTIVITY_NAME_ENDING.length) + ACTIVITY_NAME_ENDING
            : activityTitle;
    };

    public getUserFriendlyTypologyLabel(
        typology: { id: string; label: TypologyLabel },
        rawActivity: ActivityGranule
    ): string {
        let label: string = typology?.label;

        if (label === TypologyLabel.shortAnswer) {
            const shortAnswerActivity = rawActivity as ShortAnswerActivityGranule;
            if (shortAnswerActivity.attributes.reference.config?.longAnswer) {
                label = 'long_answer';
            }
        }

        return 'pretty_activity.typology_' + label + '_label';
    }

    public isActivityValid(): boolean {
        if (!this.typology) {
            console.error('Error typology with granule : ' + this.activity.id);
            return false;
        }
        if (this.typology?.label === TypologyLabel.fillBlanks) {
            const fillBlanksContent = this.activityContent as FillBlanksContent;
            const blanksCount = (fillBlanksContent.answer_text.match(/\[\]/g) || []).length;
            if (blanksCount !== fillBlanksContent.answers.length) {
                console.error('Error number of blanks with fillBlank granule : ' + this.activity.id);
                return false;
            }
        }
        return true;
    }

    public getActivitylanguage() {
        const instructionLanguage = get(this.activityReference, 'config.language.instruction');
        const wordingLanguage = get(this.activityReference, 'config.language.wording');
        return instructionLanguage ?? wordingLanguage ?? DEFAULT_LANGUAGE
    }

    private getActivityDescription() {
        if (this.typology.label !== TypologyLabel.multimedia) {
            return [this.wording, this.instruction];
        }

        // Pour les multimédias, on ne devrait afficher que le champ 'instruction', mais pour les 2 images le champ instruction est échangé avec le wording
        if (this.isTwoImagesMediaActivity())
            return [this.wording];

        return [this.instruction]
    }

    private isTwoImagesMediaActivity(): boolean {
        const multimediaContent =  (this.activity as MultimediaActivityGranule).attributes.reference.activity_content
        const mediaGranules = multimediaContent?.length >= 1
            ? multimediaContent[0].granule
            : []
        return mediaGranules?.length >= 2 && mediaGranules.every((media) => media?.file?.type === 'image');
    }
}